<script setup lang="ts">
import {reactive, ref, watch} from "vue"
import {SearchEntity} from "@/models/search.model";
import {LngLat, LngLatBounds, Popup} from "mapbox-gl";
import {MapboxMap, MapboxCluster, MapboxMarker, MapboxPopup} from '@studiometa/vue-mapbox-gl';
import {getVisibleTextColor} from "@/helpers/text.helper";
import {HexColor} from "~/types/HexColor";
import ClubSearchMarker from "@/components/SearchMap/ClubSearchMarker.vue";
import ActivitySearchMarker from "@/components/SearchMap/ActivitySearchMarker.vue";
import {Feature, MapData} from "~/types/Mapbox";
import {MarkerData} from "@/repositories/activiteit.repository";

const props = defineProps<{
    initialZoom: number,
    initialCenter: LngLat,
    maxZoom: number,
    bounds: LngLatBounds,
    markers: SearchEntity<MarkerData>[],
    location?: LngLat,
    activityPlural: string,
    activitySingular: string
}>();

const state = reactive<{
    version: number
    map?: mapboxgl.Map,
    selectedMarkerData?: MarkerData,
    mapData: MapData<MarkerData>,
}>({
    version: 0,
    mapData: {
        type: 'FeatureCollection',
        features: [],
    }
});

const mapRef = ref<mapboxgl.Map | null>(null);

 
const emits = defineEmits<{
    (e: "mapChange", center: LngLat, distance: number): void,
    (e: "markerSelected", marker: MarkerData): void,
}>();

const slots = defineSlots<{
    activiteitInfo: { marker?: MarkerData },
    aanbiederInfo: { marker?: MarkerData }
}>();

const backgroundColor = window.getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() as HexColor;
const textColor = getVisibleTextColor(backgroundColor);

const unclusteredPointPaint = {'circle-color': backgroundColor, 'circle-radius': 8, 'circle-stroke-color': textColor, 'circle-stroke-width': 2};
const clustersPaint = {'circle-color': backgroundColor, 'circle-radius': 20, 'circle-blur': 0.25};
const clusterCountPaint = {'text-color': textColor};

const setMap = (mapInstance: any) => {
    mapRef.value = mapInstance;
    onIdle();
}

const onIdle = () => {
    const map = mapRef.value;
    const bounds = map?.getBounds();
    if (!bounds) {
        return;
    }

    const leftCenterBound = new LngLat(bounds.getWest(), bounds.getCenter().lat);
    const distance = leftCenterBound.distanceTo(bounds.getCenter()) / 1000;

    emits('mapChange', bounds.getCenter(), distance);
}

const markersToFeatures = async (markers: SearchEntity<MarkerData>[]) => {
    return markers.map(itemToGeoJsonFeature);
}

const itemToGeoJsonFeature = (mapItem: SearchEntity<MarkerData>): Feature<MarkerData> => {
    return {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [mapItem.Entity.Longitude, mapItem.Entity.Latitude],
        },
        properties: mapItem.Entity,
    };
}

const onFeatureClick = (feature: Feature<MarkerData>) => {
    state.selectedMarkerData = feature.properties;
}

const onClusterClick = (cluster: any, event: any) => {
    const map = mapRef.value;
    if (map) {
        const currentZoom = map.getZoom();
        if (currentZoom < props.maxZoom) {
            const newZoom = currentZoom + 1 <= props.maxZoom ? currentZoom + 1 : props.maxZoom
            map.flyTo({center: event.lngLat, zoom: newZoom});
        } else {
            const popup = new Popup({closeOnMove: true})
            popup.setLngLat(event.lngLat);
            popup.setHTML("<h2 class=\"h5\">Meedere activiteiten op deze locatie</h2><p>Scroll naar onder om de resultaten te zien</p>");
            popup.addTo(map)
        }
    }
}

const onCloseClick = () => {
    state.selectedMarkerData = undefined;
}

watch(() => props.location, (newValue) => {
    if (newValue) {
        mapRef.value?.flyTo({center: newValue, zoom: 12});
    }
});

watch(() => props.markers, async (newValue) => {
    state.mapData.features = await markersToFeatures(newValue);
    state.version++;
}, {immediate: true});
</script>

<template>
    <MapboxMap class="search-map mb-3"
                accessToken="pk.eyJ1IjoiZG90bW92ZSIsImEiOiJjbTAyNjF0cjIwNG9oMmpzbDVrb3ZiNHR2In0.RmNzyJTv5FDF3bAlBkSxfg"
                :center="props.initialCenter" :zoom="props.initialZoom" :maxZoom="props.maxZoom" :minZoom="props.initialZoom" :maxBounds="props.bounds"
                :mapStyle="'mapbox://styles/mapbox/standard'" :attributionControl="false"
                @mb-created="setMap" @mb-moveend="onIdle" @mb-zoomend="onIdle">
        <MapboxCluster :key="state.version" :data="state.mapData"
                        :unclustered-point-paint="unclusteredPointPaint"
                        :clustersPaint="clustersPaint"
                        :clusterCountPaint="clusterCountPaint"
                        :clusterRadius="50" :clusterMaxZoom="999"
                        @mb-feature-click="onFeatureClick"
                        @mb-cluster-click="onClusterClick"/>
        <MapboxMarker v-if="props.location" :lngLat="[props.location.lng, props.location.lat]" :options="{color: '#ff0000'}" />
        <MapboxPopup :key="state.selectedMarkerData.Longitude" v-if="state.selectedMarkerData" :lngLat="[state.selectedMarkerData.Longitude, state.selectedMarkerData.Latitude]" @mb-close="onCloseClick">
            <ClubSearchMarker v-if="state.selectedMarkerData.SportaanbiederId"
                                :id="state.selectedMarkerData.SportaanbiederId" :activity-plural="activityPlural"
                                :activity-singular="activitySingular" class="search-map--info" />
            <ActivitySearchMarker v-else-if="state.selectedMarkerData.SportmomentId"
                                    :id="state.selectedMarkerData.SportmomentId" :activity-plural="activityPlural"
                                    :activity-singular="activitySingular" class="search-map--info" />
        </MapboxPopup>
    </MapboxMap>
</template>
